import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Box, 
    Typography, 
    Grid, 
    Container, 
    Breadcrumbs, 
    Link, 
    IconButton, 
    CircularProgress, 
    useTheme, 
    Divider, 
    Chip, 
    Tooltip, 
    Avatar 
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from '../../MiniBot';
import { useAuth } from '../../../helpers/AuthProvider';
import type { SxProps, Theme } from '@mui/material/styles'; 

// --- Interfaces ---
interface CostLabelPair {
    cost: string;
    label: string;
}

interface ConsumerGroupData {
    id: string;
    name: string;
    description?: string;
    rate?: string;
    burst?: string;
    quotaRequests?: string;
    quotaPeriod?: string;
    status: string;
    reasonForRejection?: string;
    costLabelPairs: CostLabelPair[];
    consumerGroupOwner: { id: string; name: string; };
}

// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | null ; sx?: SxProps<Theme>;}> = ({ label, value }) => {
    const displayValue = value || '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{displayValue}</Typography>
        </Box>
    );
};

// --- Main Content Component ---
const ConsumerGroupDetailsPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [consumerGroup, setConsumerGroup] = useState<ConsumerGroupData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();
    const [repoUrl, setRepoUrl] = useState<string | null>(null);

    useEffect(() => {
        const fetchConsumerGroupData = async () => {
            if (!id) {
                setError("Consumer Group ID is missing from URL.");
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const query = `
                    query GetConsumerGroup($getConsumerGroupId: String!) {
                        getConsumerGroup(id: $getConsumerGroupId) {
                            id
                            name
                            description
                            rate
                            burst
                            quotaRequests
                            quotaPeriod
                            status
                            reasonForRejection
                            costLabelPairs { cost, label }
                            consumerGroupOwner { id, name }
                        }
                    }
                `;
                const response = await queryGraphql(query, { getConsumerGroupId: id });
                
                if (response.getConsumerGroup) {
                    setConsumerGroup(response.getConsumerGroup);
                } else {
                    setError("Consumer Group not found or an error occurred.");
                }
            } catch (err: any) {
                setError(err.message || "Failed to fetch Consumer Group details.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchConsumerGroupData();
    }, [id]);
    
    useEffect(() => {
        const fetchRepoUrl = async () => {
            if (!consumerGroup || consumerGroup.status !== 'active' || !consumerGroup.id) return;

            try {
                const data = await queryGraphql(
                    `query Query($resourceStatusId: String!) {
                    resourceStatus(id: $resourceStatusId) {
                        projectUrl
                    }
                    }`, 
                    { resourceStatusId: consumerGroup.id }
                );
                if (data.resourceStatus?.projectUrl) {
                    setRepoUrl(data.resourceStatus.projectUrl);
                }
            } catch (error) {
                console.log("Error fetching repository URL:", error);
            }
        };

        fetchRepoUrl();
    }, [consumerGroup]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>Loading Consumer Group details...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography color="error" sx={{ p: 2 }}>{error}</Typography>;
    }

    if (!consumerGroup) {
        return <Typography sx={{ p: 2 }}>No Consumer Group data available.</Typography>;
    }

    const getStatusBackgroundColor = (status: string): string => {
        const lowerStatus = status.toLowerCase();
        if (lowerStatus === "active" || lowerStatus === "approved") return "#12B76A10";
        if (lowerStatus === "rejected") return "#F0443810";
        if (lowerStatus === "pending") return "#FDB02210";
        return "#F8F8F8";
    };

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/consumer-group" onClick={(e) => { e.preventDefault(); navigate('/consumer-group'); }}>Consumer Groups</Link>
                <Typography color="text.primary" sx={{ fontSize: "14px" }}>Consumer Group Details</Typography>
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/consumer-group')}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold', fontSize: "18px" }}>Consumer Group Details</Typography>
            </Box>

            <Box sx={{ boxShadow: "0px 3px 6px #10101029", borderRadius: "8px", bgcolor: '#fff' }}>
                {/* Header Section */}
                <Box sx={{ p: 2.5, bgcolor: getStatusBackgroundColor(consumerGroup.status), borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
                            {consumerGroup.name}
                        </Typography>
                        {user.roles?.some((e: string) => ["developer"].includes(e)) && (consumerGroup.status ==="pending" || consumerGroup.status ==="active" ) && (
                            <IconButton sx={{ p: 0.5 }} onClick={() => navigate(`/consumer-group/edit/${id}`)}>
                                <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
                            </IconButton>
                        )}
                    </Box>
                    <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
                        Resource Owner: {consumerGroup.consumerGroupOwner.name}
                    </Typography>
                </Box>

                {/* Content Section */}
                <Box sx={{ p: 3, pt: 2.5 }}>
                    {/* Throttling & Quota Section */}
                    <Typography component="h3" sx={{ fontFamily: 'Gilroy-Medium', fontSize: '14px', pb: 1 }}>Throttling & Quota Settings</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={4} md={3}><DetailItem label="Rate Limit" value={consumerGroup.rate ? `${consumerGroup.rate} req/s` : null} /></Grid>
                        <Grid item xs={6} sm={4} md={3}><DetailItem label="Burst Limit" value={consumerGroup.burst ? `${consumerGroup.burst} req/s` : null} /></Grid>
                        <Grid item xs={6} sm={4} md={3}><DetailItem label="Quota" value={consumerGroup.quotaRequests ? `${consumerGroup.quotaRequests} / ${consumerGroup.quotaPeriod}` : null} /></Grid>
                        {repoUrl && (
                             <Grid item xs={6} sm={4} md={3}>
                                <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>
                                    Configuration Repo
                                </Typography>
                                <Tooltip title={<Link href={repoUrl} target="_blank" rel="noopener noreferrer" sx={{ color: '#ffffff' }}>Repository URL</Link>} slotProps={{ tooltip: { sx: { bgcolor: '#333333' } } }}>
                                    <IconButton sx={{p: 0}}>
                                        <Avatar src="/assets/info.svg" alt="info" sx={{ height: 16, width: 16, cursor: 'pointer' }}/>
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        )}
                    </Grid>
                    <Divider sx={{ mb: 2 }} />
                    {/* Description Section */}
                    <Typography component="h3" sx={{ fontFamily: 'Gilroy-Medium', fontSize: '14px', pt: 1, pb: 1 }}>Description</Typography>

                    <DetailItem label="" value={consumerGroup.description} />
            <Divider sx={{ mb: 2 }} />
                    {/* Tags / Labels Section */}
                    <Typography component="h3" sx={{ fontFamily: 'Gilroy-Medium', fontSize: '14px', pt: 1, pb: 1 }}>Tags / Labels</Typography>

                    <Box>
                        {consumerGroup.costLabelPairs.length > 0 ? (
                            consumerGroup.costLabelPairs.map(pair => (
                                <Chip key={pair.cost} label={`${pair.cost}: ${pair.label}`} sx={{ mr: 1, mb: 1 }} />
                            ))
                        ) : (
                            <Typography fontFamily="Gilroy-Regular" fontSize="14px" color="text.secondary">No tags provided.</Typography>
                        )}
                    </Box>
                </Box>
            </Box>
            
            {consumerGroup.status === 'rejected' && (
                <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                    <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                    <Divider sx={{ marginY: 2 }} />
                    <DetailItem label="Reason for Rejection" value={consumerGroup.reasonForRejection} />
                </Box>
            )}
        </Container>
    );
};

// --- Main Page Component ---
const ConsumerGroupDetailsPage: React.FC = () => {
    const theme = useTheme();
    const getInitialDrawerState = () => {
        return typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('isFaradayDrawerOpen') || 'true') : true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);
    const handleDrawerStateChange = (isOpen: boolean) => setIsDrawerActuallyOpen(isOpen);
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"apis-consumer-group"} onStateChange={handleDrawerStateChange} />
            <MiniBot />
            <Box component="main" sx={{
                flexGrow: 1,
                p: 0,
                transition: theme.transitions.create('margin', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                marginLeft: { xs: 0, sm: `${currentMarginLeft}px` },
            }}>
                <ConsumerGroupDetailsPageContent />
            </Box>
        </Box>
    );
};

export default ConsumerGroupDetailsPage;